import { useMemo } from 'react'
import { getTopHintCategory, type HintCategory } from '@fs/zion-research-help'
import { useFeatureFlag } from '@fs/zion-flags'
import useMyHeritageHints from '../../data-service/useMyHeritageHints'
import useShowMyHeritageHints from '../../data-service/useShowMyHeritageHints'
import { getDataProblemSuggestions, getResearchSuggestionSuggestions } from '../../data-service/researchHelpHelpers'
import type { ResearchHelpData } from '../../data-service/useResearchHelpData'
import type { HintCounts } from './hintCategories'
import { EMPTY_HINT_COUNTS, getTotalHintCount, preventsTempleWork } from './hintCategories'

export type PersonHintSummary = {
  counts: HintCounts
  /**
   * Highest-priority category the person has, or undefined when they have none — the icon renders
   * nothing. The ordering lives in `@fs/zion-research-help` so this badge and the person-banner
   * indicator agree on which hint matters most.
   */
  topCategory: HintCategory | undefined
  /**
   * The number for the hints tab: the rows the patron can see in the list. With the badge on it is
   * the total across the six categories, which is what the badge reflects; with it off it is the
   * pre-badge research-help count plus MyHeritage. Neither includes tree-to-tree matches, which
   * have no icon category — the caller adds those.
   */
  total: number
}

type Props = {
  researchHelpData: ResearchHelpData
}

/**
 * Per-category hint counts for the focus person, plus the category the hint icon should show.
 *
 * The per-category breakdown and the icon are gated by treeWeb_personR9_tabHintIcons; with the flag
 * off the summary reports the count the tab showed before the badge existed, plus MyHeritage, which
 * ships on its own lab flag and so cannot wait on this one.
 *
 * Everything here is already fetched elsewhere on the person page — research help comes in through
 * ResearchHelpContext, and the MyHeritage hook is SWR-backed, so it dedupes with the research help
 * card rather than issuing an extra request.
 */
export default function usePersonHintSummary({ researchHelpData }: Props): PersonHintSummary {
  const showHintIcon = useFeatureFlag('treeWeb_personR9_tabHintIcons').isOn
  // MyHeritage carries its own gate — the partner flag plus a living/visibility check on the person
  // — and the badge must honour it, or it would both count hints the page is not allowed to show
  // and send people to MyHeritage who were never eligible.
  const showMyHeritageHints = useShowMyHeritageHints()
  // The request follows the partner gate alone, not the badge flag: the hints tab counts MyHeritage
  // whether or not the icon is on, and the research help card and section on this same page already
  // ask for it through the same SWR key, so this adds no request of its own.
  const { data: myHeritageData } = useMyHeritageHints(showMyHeritageHints)
  // One row, not the match total. However many matches the partner reports, the section renders a
  // single row, and every other category here counts the rows the patron can see.
  const myHeritageRows = (myHeritageData?.totalHints ?? 0) > 0 ? 1 : 0

  return useMemo(() => {
    // researchHelpData.count predates the partner integration, so MyHeritage is added on top of it.
    if (!showHintIcon) {
      return { counts: EMPTY_HINT_COUNTS, topCategory: undefined, total: researchHelpData.count + myHeritageRows }
    }

    const duplicateCount = researchHelpData.duplicatePersonDataProblems?.length ?? 0
    // getDataProblemSuggestions folds the possible duplicates in with the rest; they rank on their
    // own, so drop them back out before splitting the remainder by temple impact.
    const dataProblems = getDataProblemSuggestions(researchHelpData).filter(
      (problem) => problem.type?.name !== 'POSSIBLE_DUPLICATE_PERSON'
    )
    const templeBlocking = dataProblems.filter(preventsTempleWork)

    const counts: HintCounts = {
      ...EMPTY_HINT_COUNTS,
      'possible-duplicates': duplicateCount + templeBlocking.length,
      // Reference Tree (ATB) hints are deliberately left out of the badge for now. The category
      // keeps its place in @fs/zion-research-help's priority order, so surfacing it later is a
      // matter of counting useAtbHints' addable people here — see FamiliesStack for how that hook
      // is gated on treeWeb_personR9_referenceTreeHints.
      'data-problems': dataProblems.length - templeBlocking.length,
      'record-hints': researchHelpData.recordHints?.length ?? 0,
      'myheritage-matches': myHeritageRows,
      'research-suggestions': getResearchSuggestionSuggestions(researchHelpData).length,
    }

    return { counts, topCategory: getTopHintCategory(counts), total: getTotalHintCount(counts) }
  }, [myHeritageRows, researchHelpData, showHintIcon])
}
